import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeInDown, FadeOutDown } from 'react-native-reanimated';

import {
  LoadingAnimation,
  ScreenHeader as GarnishScreenHeader,
  theme,
  useElementVisibility,
  useIsFocused,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';
import { ReorderRail } from '@sg/shared-fe-features-menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';
import { useGroupOrderIntroState, useGroupOrderingContent } from '@order/features-group-ordering';
import {
  getBannerContent,
  type HomeContent,
  HomeProductCategory,
  HomeV2,
  useHomeContainerSpacing,
  useHomeContent,
  useHomeFiltering,
  useHomeLastCanceledOrder,
  useHomeMenu,
  useHomePendingFeedbackOrder,
} from '@order/features-home';
import { FlexEntryPoint, useFlexEntryPoint } from '@order/features-payments';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useDeliveryDisclosure } from '@order/shared-hooks';
import { useTelemetry } from '@order/Telemetry';

import { AppFooter, MenuReorderRail, ScreenHeader, useMenuContent } from '../../../../components';
import { GroupOrderIntroModal } from '../../../../components/GroupOrderIntroModal';
import {
  getLastInteractedDeliveryDetails,
  useDietaryPreferencesMachine,
  useLastInteractedStore,
  useLastInteractedStoreMenuHref,
} from '../../../../context';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import {
  useLastOrderStatus,
  useMenuReorderRailOrders,
  useOneClickAddToBag,
  usePendingOrder,
} from '../../../../hooks';
import { isPendingOrder, isPendingOrderStatus } from '../../utils';
import { HomeScreenPostOrder } from './HomeScreenPostOrder';

export const HomeScreenContentV2 = () => {
  const isLoggedIn = useIsLoggedIn();
  const isFocused = useIsFocused();
  const router = useRouter();
  const { track } = useTelemetry();
  const { currentBreakpoint: { isXS, isLG }, match } = useResponsive(); // prettier-ignore

  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  // ─── Group Order Intro ─────────────────────────────────────────────────────

  const handleIntroComplete = useCallback(() => {
    router.navigate('/group-ordering/create');
  }, [router]);

  const handleIntroShow = useCallback(() => {
    track('group_ordering.intro.shown');
  }, [track]);

  const {
    visible: isIntroVisible,
    show: showIntro,
    hide: hideIntro,
    dismiss: dismissIntro,
  } = useGroupOrderIntroState({
    onShow: handleIntroShow,
    onComplete: handleIntroComplete,
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerSpacingStyle = useHomeContainerSpacing();

  // So that the banner takes up 70% of the height of the screen on larger viewports.
  const bannerHeight = match([null, webOnlyStyles({ minHeight: '70svh' as never })]);

  // ─── Menu ──────────────────────────────────────────────────────────────────

  const lastInteractedStore = useLastInteractedStore() ?? {};
  const { restaurantId, restaurantSlug = '', restaurantName, addressId } = lastInteractedStore;
  const deliveryDetails = getLastInteractedDeliveryDetails(lastInteractedStore);
  const hasLocation = restaurantSlug || deliveryDetails?.addressId;
  const menuHref = useLastInteractedStoreMenuHref();

  const { restaurant, products, isLoadingMenu } = useHomeMenu({ restaurantId, addressId });
  const { deliveryDisclosure } = useDeliveryDisclosure({
    restaurantDeliveryFee: restaurant?.deliveryFee,
  });
  const pendingOrder = usePendingOrder({
    requestPolicy: 'cache-and-network',
    withPolling: true,
  });

  // ─── Allergens ─────────────────────────────────────────────────────────────

  const { restrictions: dietaryPreferences } = useDietaryPreferencesMachine();

  // ─── Content ───────────────────────────────────────────────────────────────

  const entry = useContentfulContentTypeEntry<HomeContent>({ contentType: 'homeScreen', include: 2 }); // prettier-ignore
  const content = useHomeContent(entry?.data?.fields);
  const isLoadingContent = entry?.fetching;

  const { mappedContent: groupOrderingContent } = useGroupOrderingContent({ pause: !isFocused });

  // ─── Filtering ─────────────────────────────────────────────────────────────

  const { mappedContent: { caloriesOverrides } } = useMenuContent(); // prettier-ignore
  const { filterableCategories, genericCategories } = useHomeFiltering({
    products,
    filterableCategories: content.filterableCategories,
    genericCategories: content.genericCategories,
    caloriesOverrides,
  });

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const filterableCategoriesRef = useRef<View>(null);
  const genericCategoriesRef = useRef<View>(null);

  // ─── State ─────────────────────────────────────────────────────────────────

  const [selectedCategory, setSelectedCategory] = useState<HomeProductCategory | null>(null);
  const hasSelectedCategory = Boolean(selectedCategory) && filterableCategories.length > 0;

  // ─── Flags ─────────────────────────────────────────────────────────────────

  const shouldRenderGenericCategories = genericCategories.length > 0 && !hasSelectedCategory;
  const shouldRenderFallbackCategories = genericCategories.length === 0 && !hasSelectedCategory;

  const isFilterableCategoriesVisible = useElementVisibility(filterableCategoriesRef, {
    isEnabled: !isXS && hasSelectedCategory,
  });
  const isGenericCategoriesVisible = useElementVisibility(genericCategoriesRef, {
    isEnabled: !isXS && shouldRenderGenericCategories,
  });

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCategorySelection = useCallback(
    (category: HomeProductCategory | null) => {
      track('home.filter.selected', { filterName: category?.categoryName ?? null });
      setSelectedCategory(category);
    },
    [track],
  );

  // ─── Reorder ───────────────────────────────────────────────────────────────

  const { orders, mostRecentLineItems, mostRecentOrder, isFetchingMostRecentOrders } =
    useMenuReorderRailOrders({
      restaurantId: Number.isNaN(Number(restaurantId)) ? 0 : Number(restaurantId),
      enabled: isLoggedIn && Boolean(restaurantId),
      maxItems: 5,
    });

  // Filtering should only hide the reorder rail on XS.
  const shouldHideContentOnXS = Boolean(selectedCategory) && isXS;

  const canRenderReorderRail =
    !shouldHideContentOnXS &&
    restaurantId &&
    hasLocation &&
    restaurantName &&
    mostRecentLineItems.length > 0;

  // ─── Pending Order ───────────────────────────────────────────────────────
  // Loads the last order and its order status from cache (requested on home screen).

  const lastOrderStatus = useLastOrderStatus({ requestPolicy: 'cache-only' });
  const isLoadingLastOrderStatus = lastOrderStatus?.isLoadingLastOrder;
  const hasPendingOrder = isPendingOrderStatus(lastOrderStatus?.orderStatus);

  // ─── Multiple Pending Orders ───────────────────────────────────────────────
  // Extra requirement to show when there are multiple pending orders.

  const pendingOrders = useMemo(() => orders.filter(isPendingOrder), [orders]);
  const hasMultiplePendingOrders = pendingOrders.length > 1;

  // ─── Last Canceled Order ───────────────────────────────────────────────────

  const { lastCanceledOrderId, hasRecentCanceledOrder } = useHomeLastCanceledOrder({ pause: !isLoggedIn || !isFocused }); // prettier-ignore

  // ─── Pending Feedback ──────────────────────────────────────────────────────

  const { rateableOrder, isLoadingFeedback } = useHomePendingFeedbackOrder({
    orderId: mostRecentOrder?.id,
    isLoggedIn,
  });

  const canRenderRateOrderCard = isLoggedIn && rateableOrder && !shouldHideContentOnXS;

  // ─── Flex ──────────────────────────────────────────────────────────────────

  const { shouldRenderFlexEntryPoint, checkFlexEligibility, dismissFlexEntryPoint } = useFlexEntryPoint({ isLoggedIn }); // prettier-ignore

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const { addProductToBag } = useOneClickAddToBag({
    restaurantSlug,
    deliveryDetails,
  });

  const handleGroupOrderCtaPress = useCallback(() => {
    track('group_ordering.entry_point.tapped');
    showIntro();
  }, [showIntro, track]);

  const handleGroupOrderIntroPrimaryCtaPress = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
  }, [track]);

  const handleGroupOrderIntroSecondaryCtaPress = useCallback(() => {
    track('group_ordering.intro.secondary_cta.tapped');
    track('group_ordering.intro.dismissed');

    dismissIntro();
  }, [dismissIntro, track]);

  const handleStartGroupOrder = useCallback(() => {
    track('group_ordering.intro.primary_cta.tapped');
    track('group_ordering.intro.dismissed');

    hideIntro();
  }, [hideIntro, track]);

  const openMenu = useCallback(() => {
    router.navigate(hasLocation ? '/menu' : '/location-selection');
  }, [router, hasLocation]);

  const trackMenuView = useCallback(() => {
    track('home.view_menu.selected');
  }, [track]);

  const openProductDetails = useCallback(
    (slug: string) => {
      if (deliveryDetails?.addressId) {
        router.navigate({
          pathname: '/delivery/[addressId]/[productSlug]',
          params: { addressId: deliveryDetails.addressId, productSlug: slug },
        });
        return;
      }

      router.navigate({
        pathname: '/[restaurantSlug]/[productSlug]',
        params: { restaurantSlug, productSlug: slug },
      });
    },
    [deliveryDetails?.addressId, restaurantSlug, router],
  );

  const handleAddProductToBag = useCallback(
    (id: string, name: string) => {
      track('home.product.added', { id, name });

      return addProductToBag({ id, name }, 1);
    },
    [addProductToBag, track],
  );

  const viewCanceledOrder = useCallback(() => {
    if (!lastCanceledOrderId) return;

    router.navigate(`/order-status/${lastCanceledOrderId}`);
  }, [router, lastCanceledOrderId]);

  const rateOrder = useCallback(() => {
    if (!rateableOrder) return;

    track('home.post_order.rate_order.selected');
    router.navigate(`/feedback/${rateableOrder.id}`);
  }, [router, track, rateableOrder]);

  const trackItemSelection = useCallback(
    (product: string, category: string) => {
      track('home.product.selected', { product, category });
    },
    [track],
  );

  const disclaimer =
    addressId && restaurant?.showDeliveryFeeDisclosure ? deliveryDisclosure : undefined;

  const isProductCategoryVisible = isFilterableCategoriesVisible === true || isGenericCategoriesVisible === true; // prettier-ignore

  const shouldShowDisclaimer = Platform.select({
    web: Boolean(disclaimer && isProductCategoryVisible),
    default: Boolean(disclaimer),
  });

  // ───────────────────────────────────────────────────────────────────────────

  const footerStyles = useMemo(() => {
    if (hasRecentCanceledOrder && disclaimer) {
      return styles.footerWithTwoBottomNotices;
    }

    if (hasRecentCanceledOrder || disclaimer) {
      return styles.footerWithOneBottomNotice;
    }
  }, [disclaimer, hasRecentCanceledOrder]);

  // ───────────────────────────────────────────────────────────────────────────

  if (isLoadingContent || isLoadingMenu || isFetchingMostRecentOrders || isLoadingLastOrderStatus) {
    return <LoadingAnimation />;
  }

  if (hasPendingOrder || hasMultiplePendingOrders) {
    return (
      <HomeScreenPostOrder
        content={content}
        pendingOrder={pendingOrder}
        lastOrderStatus={lastOrderStatus}
        pendingOrders={pendingOrders}
        hasPendingOrder={hasPendingOrder}
        hasMultiplePendingOrders={hasMultiplePendingOrders}
      />
    );
  }

  return (
    <>
      {isNavbarV2Enabled && isXS ? (
        <ScreenHeader
          disclaimer={disclaimer}
          pendingOrder={pendingOrder}
          rounded={false}
          shouldShowLocationTooltip
        />
      ) : null}

      <HomeV2.Container stickyHeaderIndices={hasRecentCanceledOrder ? [0] : []}>
        {hasRecentCanceledOrder && isXS ? (
          <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
        ) : null}

        <HomeV2.Container.Content>
          <HomeV2.Banner
            style={bannerHeight}
            size={isLoggedIn ? 'small' : 'large'}
            content={getBannerContent(content, isLoggedIn, isXS)}
            secondaryCtaLabel={
              isGroupOrderingEnabled ? groupOrderingContent.entryPointCtaLabel : undefined
            }
            onSecondaryCtaPress={handleGroupOrderCtaPress}
          />

          {canRenderReorderRail && !isXS ? (
            <HomeV2.ReorderRail.Container>
              {isLG ? (
                <ReorderRail.Header style={styles.reorderRailHeader}>
                  <ReorderRail.Heading />
                </ReorderRail.Header>
              ) : (
                <ReorderRail.Header>
                  <HomeV2.ReorderRail.SideWrapper />

                  <ReorderRail.Heading />

                  <HomeV2.ReorderRail.SideWrapper>
                    <MenuReorderRail.SeeAll
                      restaurantId={restaurantId}
                      deliveryDetails={deliveryDetails}
                    />
                  </HomeV2.ReorderRail.SideWrapper>
                </ReorderRail.Header>
              )}

              <MenuReorderRail.List
                location="home"
                contentContainerStyle={[containerSpacingStyle, styles.reorderRailList]}
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              >
                {isLG ? (
                  <View style={styles.seeAllItem}>
                    <MenuReorderRail.SeeAll
                      restaurantId={restaurantId}
                      deliveryDetails={deliveryDetails}
                    />
                  </View>
                ) : null}
              </MenuReorderRail.List>
            </HomeV2.ReorderRail.Container>
          ) : null}

          {!isXS && canRenderRateOrderCard ? (
            <HomeV2.RateOrderCard
              image={rateableOrder.lineItems[0]?.product?.asset?.url}
              orderCount={rateableOrder.lineItems.length}
              isLoading={isLoadingFeedback}
              onPress={rateOrder}
            />
          ) : null}

          {(canRenderReorderRail || canRenderRateOrderCard) && !isXS ? <HomeV2.Divider /> : null}

          {filterableCategories.length > 0 ? (
            <HomeV2.FilterableCategories
              ref={filterableCategoriesRef}
              title={content.filtersTitle}
              filterableCategories={filterableCategories}
              dietaryPreferences={dietaryPreferences}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelection}
              openMenu={openMenu}
              openProductDetails={openProductDetails}
              addProductToBag={handleAddProductToBag}
              trackItemSelection={trackItemSelection}
            />
          ) : null}

          {isXS && canRenderReorderRail ? (
            <HomeV2.ReorderRail.Container>
              <ReorderRail.Header style={containerSpacingStyle}>
                <ReorderRail.Heading />

                <MenuReorderRail.SeeAll
                  restaurantId={restaurantId}
                  deliveryDetails={deliveryDetails}
                />
              </ReorderRail.Header>

              <MenuReorderRail.List
                disableAnimation={Platform.OS !== 'web'}
                location="home"
                contentContainerStyle={[containerSpacingStyle, styles.reorderRailList]}
                lineItems={mostRecentLineItems}
                mostRecentOrder={mostRecentOrder}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                restaurantSlug={restaurantSlug}
                deliveryDetails={deliveryDetails}
              />
            </HomeV2.ReorderRail.Container>
          ) : null}

          {isXS && canRenderRateOrderCard ? (
            <HomeV2.RateOrderCard
              image={rateableOrder.lineItems[0]?.product?.asset?.url}
              orderCount={rateableOrder.lineItems.length}
              isLoading={isLoadingFeedback}
              onPress={rateOrder}
            />
          ) : null}

          {(canRenderReorderRail || canRenderRateOrderCard) && isXS ? <HomeV2.Divider /> : null}

          {shouldRenderGenericCategories ? (
            <HomeV2.Container.AnimatedContent>
              <HomeV2.GenericCategories
                ref={genericCategoriesRef}
                genericCategories={genericCategories}
                dietaryPreferences={dietaryPreferences}
                openMenu={openMenu}
                openProductDetails={openProductDetails}
                addProductToBag={handleAddProductToBag}
                trackItemSelection={trackItemSelection}
              />
            </HomeV2.Container.AnimatedContent>
          ) : null}

          {shouldRenderFallbackCategories ? (
            <HomeV2.FallbackCategories
              dietaryPreferences={dietaryPreferences}
              openMenu={openMenu}
              openProductDetails={openProductDetails}
              addProductToBag={handleAddProductToBag}
              trackItemSelection={trackItemSelection}
            />
          ) : null}

          <HomeV2.Footer
            menuHref={menuHref}
            title={content.footerTitle}
            imageUrl={content.footerImageUrl}
            imageA11y={content.footerImageA11y}
            trackMenuView={trackMenuView}
          />
        </HomeV2.Container.Content>

        {isXS ? null : <AppFooter style={footerStyles} />}
      </HomeV2.Container>

      {shouldRenderFlexEntryPoint && isXS ? (
        <FlexEntryPoint
          onCheckEligibility={checkFlexEligibility}
          onDismiss={dismissFlexEntryPoint}
        />
      ) : null}

      {!isXS ? (
        <View style={styles.bottomBannersContainer}>
          {shouldShowDisclaimer && disclaimer ? (
            <Animated.View
              entering={DISCLAIMER_ENTERING_ANIMATION}
              exiting={DISCLAIMER_EXITING_ANIMATION}
            >
              <GarnishScreenHeader.Disclaimer disclaimer={disclaimer} />
            </Animated.View>
          ) : null}

          {hasRecentCanceledOrder ? (
            <HomeV2.OrderCanceledBanner onPress={viewCanceledOrder} />
          ) : null}
        </View>
      ) : null}

      <GroupOrderIntroModal
        visible={isIntroVisible}
        content={groupOrderingContent}
        onPrimaryCtaPress={handleGroupOrderIntroPrimaryCtaPress}
        onSecondaryCtaPress={handleGroupOrderIntroSecondaryCtaPress}
        onStartOrder={handleStartGroupOrder}
      />
    </>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SEE_ALL_ITEM_SIZE = 100;
const DISCLAIMER_ENTERING_ANIMATION = FadeInDown.duration(theme.transitions.short);
const DISCLAIMER_EXITING_ANIMATION = FadeOutDown.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bottomBannersContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed,
  },
  reorderRailHeader: {
    justifyContent: 'center',
  },
  reorderRailList: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  seeAllItem: {
    flex: 1,
    minWidth: SEE_ALL_ITEM_SIZE,
    marginRight: -SEE_ALL_ITEM_SIZE,
    padding: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerWithOneBottomNotice: {
    paddingBottom: theme.spacing['20'],
  },
  footerWithTwoBottomNotices: {
    paddingBottom: theme.spacing['35'],
  },
});
